import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { Layout } from 'react-grid-layout'

import { PermitFieldType, SitePermitStatus } from '~/client/graph'
import { CompaniesByProjectIdDocument } from '~/client/graph/operations/generated/Companies.generated'
import { GetFormCategoriesDocument } from '~/client/graph/operations/generated/FormCategories.generated'
import { LocationsByProjectIdDocument } from '~/client/graph/operations/generated/LocationType.generated'
import { GetSitePermitAggregationListDocument } from '~/client/graph/operations/generated/SitePermits.generated'
import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import { IKindBearingLocation } from '~/client/src/shared/resources/domain/locationKind'
import GraphExecutorStore from '~/client/src/shared/stores/domain/GraphExecutor.store'
import ProjectDateStore, {
  MILLISECONDS_IN_DAY,
} from '~/client/src/shared/stores/ui/ProjectDate.store'

import {
  calculateDateRange,
  calculatePreviousPeriodDateRange,
} from './Constants/DateRanges'
import { WidgetId } from './Constants/WidgetConfig'
import { DateRange, DateRangeType } from './types/Widgets'
import { calculateComparison, ComparisonData } from './utils/ComparisonUtils'
import { LayoutManager } from './utils/LayoutManager'
import { getLocationKindLabel } from './utils/locationKindLabel'

// Default labels for missing data
const UNKNOWN_LABEL = 'Unknown'
const UNKNOWN_CATEGORY_LABEL = 'Unknown Category'
const UNCATEGORIZED_LABEL = 'Uncategorized'
const UNTITLED_WORKFLOW_LABEL = 'Untitled Workflow'
const UNKNOWN_LOCATION_LABEL = 'Unknown Location'
const COMPLETED_STATUSES = new Set<SitePermitStatus>([
  SitePermitStatus.Done,
  SitePermitStatus.Ended,
  SitePermitStatus.Passed,
  SitePermitStatus.Failed,
  SitePermitStatus.Denied,
])
const EXCLUDED_ACTIVE_VS_COMPLETED_STATUSES = new Set<SitePermitStatus>([
  SitePermitStatus.Draft,
  SitePermitStatus.Deleted,
])
const COMPANY_FIELD_TYPES = new Set<PermitFieldType>([
  PermitFieldType.Company,
  PermitFieldType.CompanyInformational,
])
const USER_FIELD_TYPES = new Set<PermitFieldType>([
  PermitFieldType.Assignee,
  PermitFieldType.Requester,
  PermitFieldType.Subscriber,
  PermitFieldType.User,
])

export default class HubV2Store {
  // Data loading state
  @observable public selectedDateRangeType: DateRangeType = DateRangeType.Week
  @observable public activeDateRange: DateRange | null = null
  @observable public workflowsData: any[] = []
  @observable public previousPeriodWorkflowsData: any[] = []
  @observable public isLoading: boolean = false
  @observable public error: string | null = null

  // ID-to-name mappings
  @observable private categoryMap: Map<string, string> = new Map()
  @observable private locationMap: Map<string, string> = new Map()
  // id -> the fields its kind is read from
  @observable private locationKindMap: Map<string, IKindBearingLocation> =
    new Map()
  @observable private companyMap: Map<string, string> = new Map()

  // UI State (moved from React component state)
  @observable public layout: Layout[] = []
  @observable public isSettingsPanelOpen: boolean = false
  @observable public widgetVisibility: Record<string, boolean> = {}
  @observable public widgetChartTypes: Record<string, string> = {}
  @observable public flippedWidgets: Record<string, boolean> = {}
  @observable public downloadingWidgets: Record<string, boolean> = {}

  private graphExecutorStore: GraphExecutorStore
  private eventsStore: DesktopEventStore
  private projectDateStore: ProjectDateStore
  private fetchRequestId: number = 0

  public constructor(
    graphExecutorStore: GraphExecutorStore,
    eventsStore: DesktopEventStore,
    projectDateStore: ProjectDateStore,
  ) {
    makeObservable(this)
    this.graphExecutorStore = graphExecutorStore
    this.eventsStore = eventsStore
    this.projectDateStore = projectDateStore

    this.initializeUIState()
  }

  private initializeUIState() {
    // Load saved widget visibility
    const savedVisibility = LayoutManager.loadWidgetVisibility()
    const defaultVisibility: Record<string, boolean> = {}
    Object.values(WidgetId).forEach(id => {
      defaultVisibility[id] =
        savedVisibility[id] !== undefined ? savedVisibility[id] : true
    })
    this.widgetVisibility = defaultVisibility

    // Load saved chart types
    this.widgetChartTypes = LayoutManager.loadWidgetChartTypes()
    this.layout = LayoutManager.getLayout()
  }

  // UI State Actions
  @action
  public setLayout(layout: Layout[]) {
    this.layout = layout
  }

  @action
  public setSettingsPanelOpen(isOpen: boolean) {
    this.isSettingsPanelOpen = isOpen
  }

  @action
  public toggleSettingsPanel() {
    this.isSettingsPanelOpen = !this.isSettingsPanelOpen
  }

  @action
  public setWidgetVisibility(widgetId: string, isVisible: boolean) {
    this.widgetVisibility = { ...this.widgetVisibility, [widgetId]: isVisible }
    LayoutManager.saveWidgetVisibility(widgetId, isVisible)
  }

  @action
  public setWidgetChartType(widgetId: string, chartType: string) {
    this.widgetChartTypes = { ...this.widgetChartTypes, [widgetId]: chartType }
    LayoutManager.saveWidgetChartType(widgetId, chartType)
  }

  @action
  public toggleWidgetFlip(widgetId: string) {
    this.flippedWidgets = {
      ...this.flippedWidgets,
      [widgetId]: !this.flippedWidgets[widgetId],
    }
  }

  @action
  public setDownloadingWidget(widgetId: string, isDownloading: boolean) {
    this.downloadingWidgets = {
      ...this.downloadingWidgets,
      [widgetId]: isDownloading,
    }
  }

  @action
  public resetLayout() {
    this.layout = LayoutManager.resetLayout()
  }

  @action
  public applyPreferences(preferences: {
    layout?: Layout[]
    widgetVisibility?: Record<string, boolean>
    widgetChartTypes?: Record<string, string>
  }) {
    if (preferences.layout) {
      this.layout = preferences.layout
    }
    if (preferences.widgetVisibility) {
      this.widgetVisibility = preferences.widgetVisibility
    }
    if (preferences.widgetChartTypes) {
      this.widgetChartTypes = preferences.widgetChartTypes
    }
  }

  @computed
  public get dateRange(): DateRange {
    return (
      this.activeDateRange ||
      calculateDateRange(this.selectedDateRangeType, this.projectDateStore)
    )
  }

  @computed
  public get workflowsCount(): number {
    return this.workflowsData.length
  }

  @computed
  public get uniqueCompanies(): Set<string> {
    const companies = new Set<string>()
    this.workflowsData.forEach(workflow => {
      if (workflow.fields) {
        workflow.fields.forEach((field: any) => {
          if (
            COMPANY_FIELD_TYPES.has(field.type as PermitFieldType) &&
            field.objectIdValues &&
            field.objectIdValues.length > 0
          ) {
            field.objectIdValues.forEach((id: string) => companies.add(id))
          }
        })
      }
    })
    return companies
  }

  @computed
  public get companiesCount(): number {
    return this.uniqueCompanies.size
  }

  @computed
  public get workflowsByStatus(): Record<string, number> {
    const statusCounts: Record<string, number> = {}
    this.workflowsData.forEach(workflow => {
      const status = workflow.status || UNKNOWN_LABEL
      statusCounts[status] = (statusCounts[status] || 0) + 1
    })
    return statusCounts
  }

  @computed
  public get workflowsByCategory(): Record<string, number> {
    const categoryCounts: Record<string, number> = {}
    this.workflowsData.forEach(workflow => {
      const categoryId = workflow.categoryId
      const categoryName = categoryId
        ? this.categoryMap.get(categoryId) || UNKNOWN_CATEGORY_LABEL
        : UNCATEGORIZED_LABEL
      categoryCounts[categoryName] = (categoryCounts[categoryName] || 0) + 1
    })
    return categoryCounts
  }

  @computed
  public get workflowsByLocation(): Record<string, number> {
    const locationCounts: Record<string, number> = {}

    this.workflowsData.forEach(workflow => {
      // Extract location IDs from workflow fields
      const fields = workflow.fields || []
      fields.forEach((field: any) => {
        if (field.locationValues && Array.isArray(field.locationValues)) {
          field.locationValues.forEach((locVal: any) => {
            if (locVal && locVal.id) {
              const locationName =
                this.locationMap.get(locVal.id) || UNKNOWN_LOCATION_LABEL
              locationCounts[locationName] =
                (locationCounts[locationName] || 0) + 1
            }
          })
        }
      })
    })

    return locationCounts
  }

  @computed
  public get recentWorkflows(): Array<{
    id: string
    title: string
    status: string
    startDate: number
    categoryName: string
  }> {
    return this.workflowsData
      .filter(w => w.startDate)
      .map(w => ({
        id: w.id,
        title: w.title || UNTITLED_WORKFLOW_LABEL,
        status: w.status || UNKNOWN_LABEL,
        startDate: w.startDate,
        categoryName: w.categoryId
          ? this.categoryMap.get(w.categoryId) || UNKNOWN_CATEGORY_LABEL
          : UNCATEGORIZED_LABEL,
      }))
      .sort((a, b) => b.startDate - a.startDate) // Sort by most recent first
      .slice(0, 20) // Show latest 20 workflows
  }

  @computed
  public get workflowsTimeline(): Array<{ date: string; count: number }> {
    const timelineMap = new Map<string, number>()

    this.workflowsData.forEach(workflow => {
      if (workflow.startDate) {
        const date = new Date(workflow.startDate).toLocaleDateString()
        timelineMap.set(date, (timelineMap.get(date) || 0) + 1)
      }
    })

    return Array.from(timelineMap.entries())
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
  }

  @computed
  private get averageDurationDays(): number {
    const workflowsWithDuration = this.workflowsData.filter(
      w => w.startDate && w.endDate,
    )

    if (!workflowsWithDuration.length) return 0

    const totalDuration = workflowsWithDuration.reduce((sum, workflow) => {
      const start = new Date(workflow.startDate).getTime()
      const end = new Date(workflow.endDate).getTime()
      const durationDays = (end - start) / MILLISECONDS_IN_DAY
      return sum + durationDays
    }, 0)

    return totalDuration / workflowsWithDuration.length
  }

  @computed
  public get averageDuration(): string {
    const avgDays = this.averageDurationDays

    if (avgDays === 0) return '0'

    if (avgDays < 1) {
      const hours = Math.round(avgDays * 24)
      return `${hours} hours`
    }

    return `${avgDays.toFixed(1)} days`
  }

  @computed
  public get activeVsCompletedCount(): { active: number; completed: number } {
    let active = 0
    let completed = 0

    this.workflowsData.forEach(workflow => {
      const status = workflow.status as SitePermitStatus

      if (EXCLUDED_ACTIVE_VS_COMPLETED_STATUSES.has(status)) {
        return
      }

      if (COMPLETED_STATUSES.has(status)) {
        completed++
      } else {
        active++
      }
    })

    return { active, completed }
  }

  @computed
  public get usersCount(): number {
    const users = new Set<string>()
    this.workflowsData.forEach(workflow => {
      if (workflow.createdBy) {
        users.add(workflow.createdBy)
      }
      if (workflow.fields) {
        workflow.fields.forEach((field: any) => {
          if (
            USER_FIELD_TYPES.has(field.type as PermitFieldType) &&
            field.objectIdValues &&
            field.objectIdValues.length > 0
          ) {
            field.objectIdValues.forEach((id: string) => users.add(id))
          }
        })
      }
    })
    return users.size
  }

  @computed
  public get advancedPlanningDays(): number {
    const workflowsWithDates = this.workflowsData.filter(
      w => w.createdAt && w.startDate,
    )

    if (workflowsWithDates.length === 0) return 0

    const totalDays = workflowsWithDates.reduce((sum, workflow) => {
      const created = new Date(workflow.createdAt).getTime()
      const start = new Date(workflow.startDate).getTime()
      const days = (start - created) / MILLISECONDS_IN_DAY
      return sum + Math.max(0, days)
    }, 0)

    return Math.round(totalDays / workflowsWithDates.length)
  }

  @computed
  public get equipmentBookingsCount(): number {
    return this.workflowsData.filter(workflow => {
      if (!workflow.fields) return false
      return workflow.fields.some(
        (field: any) =>
          field.type === PermitFieldType.Equipment &&
          field.locationValues &&
          field.locationValues.length > 0,
      )
    }).length
  }

  @computed
  public get equipmentBookingsHours(): number {
    let totalHours = 0
    this.workflowsData.forEach(workflow => {
      if (!workflow.fields || !workflow.startDate || !workflow.endDate) return
      const hasEquipment = workflow.fields.some(
        (field: any) =>
          field.type === PermitFieldType.Equipment &&
          field.locationValues &&
          field.locationValues.length > 0,
      )
      if (hasEquipment) {
        const start = new Date(workflow.startDate).getTime()
        const end = new Date(workflow.endDate).getTime()
        const hours = (end - start) / (1000 * 60 * 60)
        totalHours += Math.max(0, hours)
      }
    })
    return Math.round(totalHours)
  }

  @computed
  public get locationWorkflowsData(): Record<string, number> {
    const locationCounts: Record<string, number> = {}
    this.workflowsData.forEach(workflow => {
      const location = workflow.location || UNKNOWN_LABEL
      locationCounts[location] = (locationCounts[location] || 0) + 1
    })
    return locationCounts
  }

  @computed
  public get locationsByType(): Record<string, number> {
    const typeCounts: Record<string, number> = {}
    this.workflowsData.forEach(workflow => {
      const workflowLocationTypes = new Set<string>()
      const fields = workflow.fields || []
      fields.forEach((field: any) => {
        if (field.locationValues && Array.isArray(field.locationValues)) {
          field.locationValues.forEach((locVal: any) => {
            workflowLocationTypes.add(
              getLocationKindLabel(
                locVal,
                this.locationKindMap.get(locVal?.id),
                UNKNOWN_LABEL,
              ),
            )
          })
        }
      })

      workflowLocationTypes.forEach(type => {
        typeCounts[type] = (typeCounts[type] || 0) + 1
      })
    })
    return typeCounts
  }

  // ========== PREVIOUS PERIOD METRICS ==========

  @computed
  private get previousPeriodWorkflowsCount(): number {
    return this.previousPeriodWorkflowsData.length
  }

  @computed
  private get previousPeriodCompaniesCount(): number {
    const companies = new Set<string>()
    this.previousPeriodWorkflowsData.forEach(workflow => {
      if (workflow.fields) {
        workflow.fields.forEach((field: any) => {
          if (
            COMPANY_FIELD_TYPES.has(field.type as PermitFieldType) &&
            field.objectIdValues &&
            field.objectIdValues.length > 0
          ) {
            field.objectIdValues.forEach((id: string) => companies.add(id))
          }
        })
      }
    })
    return companies.size
  }

  @computed
  private get previousPeriodUsersCount(): number {
    const users = new Set<string>()
    this.previousPeriodWorkflowsData.forEach(workflow => {
      if (workflow.createdBy) {
        users.add(workflow.createdBy)
      }
      if (workflow.fields) {
        workflow.fields.forEach((field: any) => {
          if (
            USER_FIELD_TYPES.has(field.type as PermitFieldType) &&
            field.objectIdValues &&
            field.objectIdValues.length > 0
          ) {
            field.objectIdValues.forEach((id: string) => users.add(id))
          }
        })
      }
    })
    return users.size
  }

  @computed
  private get previousPeriodAverageDuration(): number {
    const workflowsWithDuration = this.previousPeriodWorkflowsData.filter(
      w => w.startDate && w.endDate,
    )

    if (!workflowsWithDuration.length) return 0

    const totalDuration = workflowsWithDuration.reduce((sum, workflow) => {
      const start = new Date(workflow.startDate).getTime()
      const end = new Date(workflow.endDate).getTime()
      const durationDays = (end - start) / MILLISECONDS_IN_DAY
      return sum + durationDays
    }, 0)

    return Math.round(totalDuration / workflowsWithDuration.length)
  }

  @computed
  private get previousPeriodAdvancedPlanningDays(): number {
    const workflowsWithDates = this.previousPeriodWorkflowsData.filter(
      w => w.createdAt && w.startDate,
    )

    if (workflowsWithDates.length === 0) return 0

    const totalDays = workflowsWithDates.reduce((sum, workflow) => {
      const created = new Date(workflow.createdAt).getTime()
      const start = new Date(workflow.startDate).getTime()
      const days = (start - created) / MILLISECONDS_IN_DAY
      return sum + Math.max(0, days)
    }, 0)

    return Math.round(totalDays / workflowsWithDates.length)
  }

  // ========== COMPARISON METRICS ==========

  @computed
  private get isProjectRange(): boolean {
    return this.selectedDateRangeType === DateRangeType.Project
  }

  @computed
  public get workflowsCountComparison(): ComparisonData | null {
    if (this.isProjectRange) return null
    return calculateComparison(
      this.workflowsCount,
      this.previousPeriodWorkflowsCount,
    )
  }

  @computed
  public get companiesCountComparison(): ComparisonData | null {
    if (this.isProjectRange) return null
    return calculateComparison(
      this.companiesCount,
      this.previousPeriodCompaniesCount,
    )
  }

  @computed
  public get usersCountComparison(): ComparisonData | null {
    if (this.isProjectRange) return null
    return calculateComparison(this.usersCount, this.previousPeriodUsersCount)
  }

  @computed
  public get averageDurationComparison(): ComparisonData | null {
    if (this.isProjectRange) return null
    return calculateComparison(
      this.averageDurationDays,
      this.previousPeriodAverageDuration,
    )
  }

  @computed
  public get advancedPlanningComparison(): ComparisonData | null {
    if (this.isProjectRange) return null
    return calculateComparison(
      this.advancedPlanningDays,
      this.previousPeriodAdvancedPlanningDays,
    )
  }

  @action.bound
  public setDateRange(dateRange: DateRange) {
    this.selectedDateRangeType = dateRange.type
    this.activeDateRange = dateRange
    this.fetchWorkflows()
  }

  /**
   * Fetch reference data (categories, locations, companies) for name mapping
   */
  @action.bound
  private async fetchReferenceMappings() {
    const projectId = this.eventsStore?.appState?.activeProject?.id
    if (!projectId) return

    try {
      // Fetch all reference data in parallel
      const [categoriesResult, locationsResult, companiesResult] =
        await Promise.all([
          this.graphExecutorStore.query(GetFormCategoriesDocument, {
            projectId,
          }),
          this.graphExecutorStore.query(LocationsByProjectIdDocument, {
            projectId,
          }),
          this.graphExecutorStore.query(CompaniesByProjectIdDocument, {
            projectId,
          }),
        ])

      // Build category map (id -> label)
      const categories = categoriesResult.data?.formCategories?.data || []
      runInAction(() => {
        this.categoryMap.clear()
        categories.forEach((cat: any) => {
          if (cat.id && cat.label) {
            this.categoryMap.set(cat.id, cat.label)
          }
        })
      })

      // Build location map (id -> name)
      const locations = locationsResult.data?.locations?.data || []
      runInAction(() => {
        this.locationMap.clear()
        this.locationKindMap.clear()
        locations.forEach((loc: any) => {
          if (loc.id && loc.name) {
            this.locationMap.set(loc.id, loc.name)
          }
          if (loc.id) {
            this.locationKindMap.set(loc.id, { type: loc.type, data: loc.data })
          }
        })
      })

      // Build company map (id -> name)
      const companies = companiesResult.data?.companies?.data || []
      runInAction(() => {
        this.companyMap.clear()
        companies.forEach((company: any) => {
          if (company.id && company.name) {
            this.companyMap.set(company.id, company.name)
          }
        })
      })

      console.log('✅ Loaded reference mappings:', {
        categories: this.categoryMap.size,
        locations: this.locationMap.size,
        companies: this.companyMap.size,
      })
    } catch (err) {
      console.error('❌ Error fetching reference mappings:', err)
    }
  }

  @action.bound
  public async fetchWorkflows() {
    const requestId = ++this.fetchRequestId
    this.isLoading = true
    this.error = null

    try {
      const currentRange = this.dateRange
      const previousRange = calculatePreviousPeriodDateRange(
        currentRange,
        this.selectedDateRangeType,
        this.projectDateStore,
      )
      const projectId = this.eventsStore?.appState?.activeProject?.id

      if (!projectId) {
        runInAction(() => {
          if (requestId !== this.fetchRequestId) return
          this.workflowsData = []
          this.previousPeriodWorkflowsData = []
        })
        return
      }

      console.log('📅 Fetching workflows for comparison:', {
        current: currentRange.start
          ? `${currentRange.start.toLocaleDateString()} - ${currentRange.end.toLocaleDateString()}`
          : `Entire project - ${currentRange.end.toLocaleDateString()}`,
        previous: previousRange.start
          ? `${previousRange.start.toLocaleDateString()} - ${previousRange.end.toLocaleDateString()}`
          : `Entire project - ${previousRange.end.toLocaleDateString()}`,
      })

      // Fetch reference data, current period workflows, and previous period workflows in parallel
      await Promise.all([
        this.fetchReferenceMappings(),
        // Fetch current period
        (async () => {
          // For "entire project" (no start date), use epoch time (Jan 1, 1970)
          const startDateMs = currentRange.start
            ? currentRange.start.getTime()
            : 0

          const { data, error } = await this.graphExecutorStore.query(
            GetSitePermitAggregationListDocument,
            {
              filter: {
                projectId,
                startDate: startDateMs,
                endDate: currentRange.end.getTime(),
                withDeleted: false,
              },
              limit: 1_000_000,
            },
          )

          if (error) {
            throw error
          }

          runInAction(() => {
            if (requestId !== this.fetchRequestId) return
            this.workflowsData = data?.sitePermitsAggregation?.data || []
          })
        })(),
        // Fetch previous period
        (async () => {
          // For "entire project" (no start date), use epoch time (Jan 1, 1970)
          const startDateMs = previousRange.start
            ? previousRange.start.getTime()
            : 0

          const { data, error } = await this.graphExecutorStore.query(
            GetSitePermitAggregationListDocument,
            {
              filter: {
                projectId,
                startDate: startDateMs,
                endDate: previousRange.end.getTime(),
                withDeleted: false,
              },
              limit: 1_000_000,
            },
          )

          if (error) {
            throw error
          }

          runInAction(() => {
            if (requestId !== this.fetchRequestId) return
            this.previousPeriodWorkflowsData =
              data?.sitePermitsAggregation?.data || []
          })
        })(),
      ])

      console.log('✅ Successfully fetched workflows with comparison:', {
        current: {
          count: this.workflowsData.length,
          dateRange: currentRange.start
            ? `${currentRange.start.toLocaleDateString()} - ${currentRange.end.toLocaleDateString()}`
            : `Entire project - ${currentRange.end.toLocaleDateString()}`,
        },
        previous: {
          count: this.previousPeriodWorkflowsData.length,
          dateRange: previousRange.start
            ? `${previousRange.start.toLocaleDateString()} - ${previousRange.end.toLocaleDateString()}`
            : `Entire project - ${previousRange.end.toLocaleDateString()}`,
        },
      })
    } catch (err) {
      if (requestId !== this.fetchRequestId) return
      console.error('❌ Error fetching workflows:', err)
      runInAction(() => {
        this.error =
          err instanceof Error ? err.message : 'Failed to fetch workflows'
        this.workflowsData = []
        this.previousPeriodWorkflowsData = []
      })
    } finally {
      if (requestId === this.fetchRequestId) {
        runInAction(() => {
          this.isLoading = false
        })
      }
    }
  }
}
